import { useState } from 'react';
import useSWR from 'swr';
import { useNavigate, useParams } from 'react-router-dom';
import {
 Heading,
 Box,
 Container,
 Tabs,
 TabPanels,
 TabPanel,
 useToast,
} from '@chakra-ui/react';
import { InputText, Select } from '../../../../components/Input';
import { QuestionnaireHeaderNew } from '../../../../components/Form';
import { optionsNumPilares } from '../../../../resources/mock_options';
import { fetchData } from '../../../../services/fetch';
import baseService from '../../../../services/base';
import { SuccessModal } from '../../../../components/Modals';
import PillarFormEdit from './PillarFormEdit';
import TabList from '../../../../components/Certification/TabList/TabList';
import { PillarConfigList } from '../../../../components/Certification/PillarConfig/PillarConfigList';
import { useDiagnosisForm } from '../../../../hooks/useDiagnosisForm';

export default function DiagnosisEditForm({ defaultValues, data, mutate }) {
 const toast = useToast();
 const navigate = useNavigate();
 const { id: surveyId } = useParams();

 const [activeTab, setActiveTab] = useState(1);
 const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
 const [imageUpdated, setImageUpdated] = useState(false);
 const [forcedUpdate, setForcedUpdate] = useState(false);

 const initialPillarCount = defaultValues?.form?.length || 0;

 const {
  showPillarsTabs,
  setShowPillarsTabs,
  pillarCount,
  setPillarCount,
  formMethods,
  fields,
  append,
  remove,
  handleNumPilaresChange,
  confirmNewPillars
 } = useDiagnosisForm({
  isEditing: true,
  initialData: defaultValues,
 });

 const {
  handleSubmit,
  register,
  control,
  setValue,
  trigger,
  formState: { errors, isValid, isDirty, isSubmitting, dirtyFields },
 } = formMethods;

 const { data: templates, isLoading: templatesIsLoading } = useSWR(
  `/survey-template/all`,
  fetchData
 );

 const findEditedValuesIndex = () => {
  if (dirtyFields && dirtyFields?.form) {
   for (let i = 0; i < dirtyFields?.form?.length; i++) {
    const field = dirtyFields?.form?.[i];

    if (field && fields?.[i]) {
     const hasOtherEditedFields = Object.keys(field)?.some(
      (key) =>
       key !== 'pillarName' &&
       (field[key] === true ||
        (Array.isArray(field[key]) && field[key]?.some((subField) => subField === true)))
     );

     if (hasOtherEditedFields) {
      return i + 1;
     }
    }
   }
  }
  return 1;
 };

 function handlePillarsConfig() {
  confirmNewPillars(); 
  append({
   pillarName: '',
   numberOfThemes: '',
   numberOfOptions: '',
   hidden: true,
  });

  setTimeout(() => {
   remove(fields?.length);
  }, 1000);
  setShowPillarsTabs(true);
  setActiveTab(findEditedValuesIndex());
 }

 const handleTabsChange = (index) => {
  setActiveTab(index);
 };

 const showErrorToast = (message) => {
  toast({
   title: 'Erro',
   description: message,
   status: 'error',
  });
 };

 const handlePillarCountChange = (e) => {
  const newCount = parseInt(e.target.value);
  handleNumPilaresChange(newCount);
  setPillarCount(newCount);
  setActiveTab(0);
  setForcedUpdate(true);
 };

 const onSubmit = async (values) => {
  const { name, form } = values;
  try {
   if (name !== defaultValues?.name) {
    await baseService.patch(`/survey/name/${surveyId}`, {
     description: name,
    });
   }


   // EG-42/AD-5: o laço que apagava pilares SAIU.
   //
   // Pós-EG-40 o pilar é do CATÁLOGO GLOBAL — a rota DELETE apagava a linha do
   // catálogo (e, no caso de /survey/pillar/:id, todos os temas dela) a partir da
   // tela de UM questionário, tirando o conceito de todos os clientes que o
   // usam. Não é dívida técnica: era perda de dado em produção.
   //
   // E, pela AD-9, remover não existe mais como operação: a classificação da
   // questão dentro do questionário é imutável em existência — tem pilar, pode
   // TROCAR; não tem, não ganha. Apagar do catálogo para "desvincular" nunca foi
   // a mesma coisa. Quem precisa aposentar um pilar faz isso na tela de gestão do
   // catálogo, em /pillars, onde apagar é legítimo.


   for (const pillarData of form) {
    const {
     pillarId,
     idPillar,
     helpTitle,
     helpText,
     themes,
     levels,
    } = pillarData;

    const defaultPillarData = defaultValues?.form?.find(
     (item) => item?.pillarId === pillarData?.pillarId
    );

    if (defaultPillarData) {
     for (const level of levels) {
      const defaultLevel = defaultPillarData?.levels.find(
       (item) => item.nivel === level.nivel
      );
      if (defaultLevel) {
       const removedQuestions = defaultLevel.questions.filter(
        (questionId) => !level.questions.includes(questionId)
       );

       if (removedQuestions.length > 0) {
        if (!level.delete) {
         level.delete = [];
        }
        level.delete.push(...removedQuestions);

        level.questions = level.questions.filter(
         (questionId) => !removedQuestions.includes(questionId)
        );
       }
      }
     }
    }

    const transformedThemes = themes?.map((theme, index) => ({
     title: theme?.title,
     index: index + 1,
     ...(theme?.id_theme && {
      id_theme: theme.id_theme,
     }),
    }));

    // EG-40: o pilar vem do catálogo global; o conteúdo editorial e os temas
    // não são mais criados aqui.
    const formValues = {
     id_pillar: idPillar,
     niveis: levels,
     id_survey: +surveyId,
    };

    if (pillarData?.new) {
     await baseService.post(
      `/survey/diagnostic/pillar`,
      JSON.stringify({
       id_survey: +surveyId,
       ...formValues,
      })
     );
    } else {
     await baseService.patch(`/survey/diagnostic/${pillarId}/pillar`, {
      ...formValues,
     });
    }
   }
   setIsSuccessModalOpen(true);
  } catch (error) {
   showErrorToast('Não foi possível salvar o questionário');
  }
 };

 return (
  <form onSubmit={handleSubmit(onSubmit)}>
   <QuestionnaireHeaderNew
    heading='Questionário de Diagnóstico ESG'
    isSubmitEnabled={(isValid && imageUpdated) || ((isDirty || forcedUpdate) && isValid)}
    isSubmit={true}
    isSubmitting={isSubmitting}
   >
    <InputText
     {...register('name')}
     error={errors.name}
     placeholder='Digite o nome do questionário'
     w='541px'
    />
   </QuestionnaireHeaderNew>

   <Container maxW='1280px' p={0} minH='80vh' my={['8px', '32px']}>
    <Box bg='white' borderRadius='12px' pt='20px'>
     <Tabs index={activeTab} onChange={handleTabsChange} pos='static'>
      <TabList
       fields={fields}
       validPillars={fields.length}
       showPillarsTabs={showPillarsTabs}
       activeTab={activeTab}
       onTabChange={setActiveTab}
      />

      <TabPanels mt='6' borderTop='2px solid #F5F5FB'>
       <PillarConfigList
        setValue={setValue}
        fields={fields}
        control={control}
        isDirty={isDirty}
        handlePillarsConfig={handlePillarsConfig}
        showNumberOfThemes={true}
        showNumberOfLevels={true}
       >
        <Select
         label='Número de pilares'
         placeholder='Selecione o número de pilares'
         options={optionsNumPilares}
         value={pillarCount}
         defaultValue={initialPillarCount}
         onChange={handlePillarCountChange}
         w='279px'
        />
       </PillarConfigList>

       {fields?.map(
        (field, index) =>
         field?.pillarName && (
          <TabPanel key={field.id} py='0' px='0'>
           <Heading
            fontSize='20px'
            fontWeight={600}
            lineHeight='23.48px'
            p='6'
            borderBottom='2px solid #F5F5FB'
           >
            {field?.pillarName}
           </Heading>

           <PillarFormEdit
            index={index}
            field={field}
            themeRef={data?.pillars?.[index]?.themes?.[0]?.id_theme}
            register={register}
            errors={errors}
            setValue={setValue}
            trigger={trigger}
            control={control}
            templates={templates}
            templatesIsLoading={templatesIsLoading}
            setImageUpdated={setImageUpdated}
            setForcedUpdate={setForcedUpdate}
           />
          </TabPanel>
         )
       )}
      </TabPanels>
     </Tabs>
    </Box>
    <SuccessModal
     isOpen={isSuccessModalOpen}
     onClose={() => {
      setIsSuccessModalOpen(false);
      mutate();
      navigate('/researches');
     }}
     title='Questionário editado com sucesso!'
    />
   </Container>
  </form>
 );
}
